import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Collapse,
  Button,
  Box,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const caseStudies = [
  {
    id: 'sofee',
    tags: ['Design Systems', 'Enterprise UX', 'User Research'],
    title: 'Sofee \u2014 Building a Scalable Design System for Social Media Forms',
    company: 'Digital Harbor \u2022 2020\u20132026',
    summary: 'Designed and maintained a 30-component design system in Figma and Storybook, while leading UX efforts across the Sofee platform \u2014 from user research and workflow mapping to interactive prototyping and iterative testing.',
    sections: [
      {
        heading: 'The Problem',
        content: "Sofee's social media forms platform had grown organically, resulting in inconsistent UI patterns, duplicated components, and user flows that varied across features. Customers reported confusion navigating multi-step form creation workflows. The development team lacked a shared design language, slowing feature delivery."
      },
      {
        heading: 'My Role',
        content: 'As Senior Front End Developer with UX ownership, I was responsible for the end-to-end design process: research, user flows, wireframing, prototyping, design system creation, usability testing, and cross-functional collaboration with multiple dev teams.'
      },
      {
        heading: 'Discovery & Research',
        items: [
          'Conducted customer interviews and usability testing sessions on existing workflows to identify pain points and drop-off patterns',
          'Mapped end-to-end user flows across the platform, identifying edge cases, system states, and multi-role interaction points',
          'Synthesized findings into actionable design principles that guided the team\'s decisions'
        ]
      },
      {
        heading: 'Design & Systems Thinking',
        items: [
          'Audited existing UI components across the platform, cataloging inconsistencies and redundancies',
          'Designed a 30-component design system in Figma with variants, states, and usage guidelines',
          'Built corresponding Storybook documentation for Angular components, ensuring design-to-code fidelity',
          'Created wireframes and interactive prototypes in Figma and Adobe XD to validate new user flows before development'
        ]
      },
      {
        heading: 'Validation & Iteration',
        items: [
          'Ran prototype testing sessions with customers, collecting feedback at each iteration',
          'Iterated on designs based on testing results and post-launch analytics',
          'Continued customer interviews on shipped features to identify improvement opportunities'
        ]
      },
      {
        heading: 'Outcome',
        items: [
          'Unified design language adopted across all Sofee product features',
          'Reduced design-to-development handoff friction through shared Storybook components',
          'Improved workflow clarity for customers through tested, validated user flows',
          'Enabled faster feature delivery by giving dev teams a consistent component library'
        ]
      }
    ],
    tools: 'Figma, Adobe XD, Storybook, Angular, GitHub Copilot, Jira, Confluence'
  },
  {
    id: 'medicfp',
    tags: ['Cross-Platform UX', 'Complex Workflows', 'User Research'],
    title: 'MedicFP \u2014 Designing Biometric Experiences Across Web, Mobile & Desktop',
    company: 'MedicFP \u2022 2014\u20132019',
    summary: 'Designed and prototyped 20+ biometric applications for healthcare, translating complex identity verification workflows into intuitive experiences across Android, iOS, and Windows platforms \u2014 validated through continuous user testing and customer feedback.',
    sections: [
      {
        heading: 'The Problem',
        content: 'Healthcare organizations needed reliable biometric identity verification (fingerprint, facial recognition) to prevent fraud and ensure patient safety. The challenge: these workflows involved sensitive data, strict compliance requirements, and users with varying technical literacy \u2014 all across three different platforms.'
      },
      {
        heading: 'My Role',
        content: 'As Lead Front End & UX/UI Developer in a startup environment, I wore multiple hats: UX research, interaction design, prototyping, front-end development, and team leadership. I owned the design process from discovery through delivery.'
      },
      {
        heading: 'Discovery & Research',
        items: [
          'Partnered with Verizon, Microsoft, and Fujitsu to understand technical constraints and hardware capabilities for biometric capture',
          'Conducted prototype testing with healthcare staff at client sites including CMS, Simply Health Care, and Jackson Memorial Hospital',
          'Mapped complex verification workflows, identifying edge cases such as failed biometric captures, fallback authentication, and multi-role access patterns'
        ]
      },
      {
        heading: 'Design & Systems Thinking',
        items: [
          'Designed adaptive user flows that worked across web, mobile, and desktop \u2014 accounting for device-specific biometric hardware and interaction patterns',
          'Created a unified visual language across Verify, Certify, Notify, and Assure product lines to maintain brand consistency while adapting to platform conventions',
          'Prototyped multi-step verification workflows, minimizing cognitive load for healthcare workers under time pressure'
        ]
      },
      {
        heading: 'Validation & Iteration',
        items: [
          'Conducted iterative prototype testing sessions with healthcare staff, refining flows based on real-world usage patterns',
          'Collected post-launch customer feedback to identify usability improvements across all four product lines',
          'Iterated on error states and fallback flows based on field testing with biometric hardware'
        ]
      },
      {
        heading: 'Outcome',
        items: [
          'Delivered 20+ applications across three platforms with consistent, tested user experiences',
          'Successfully deployed biometric verification at major healthcare institutions',
          'Reduced verification workflow complexity, improving adoption among non-technical healthcare staff',
          'Established repeatable UX patterns for biometric interactions reused across the product suite'
        ]
      }
    ],
    tools: 'Figma, Adobe XD, Sketch, Xcode, Android Studio, Scrum/Agile, Jira'
  },
  {
    id: 'ai-process',
    tags: ['AI-Assisted Design', 'Design Thinking', 'Process Documentation'],
    title: 'Meta Case Study \u2014 Using Claude to Rethink My Resume & Portfolio',
    company: 'Personal Project \u2022 February 2026',
    summary: 'A transparent look at how I used Claude (Anthropic\'s AI) as a design thinking partner to analyze a job description, identify skill gaps in my resume, conduct a structured requirements-gathering process, and produce a tailored resume and portfolio \u2014 demonstrating willingness to adopt new tools and document the thinking process.',
    sections: [
      {
        heading: 'The Problem',
        content: 'My resume was developer-focused and visually outdated. I was applying for a UX Designer role that emphasized systems thinking, design systems, UX research, and AI tool adoption. My existing resume didn\'t surface the UX work I\'d been doing for years. I needed to reframe my story \u2014 and I wanted to use AI to do it.'
      },
      {
        heading: 'The Process \u2014 Design Thinking with AI',
        steps: [
          { number: '01', title: 'Empathize: Understanding the Target', content: 'Fed Claude both my resume and the full job description. Claude performed a gap analysis, identifying the mismatch between my developer-framed experience and the UX Designer requirements.' },
          { number: '02', title: 'Define: Structured Requirements Gathering', content: 'Used an Intent Optimizer protocol \u2014 a structured clarification loop where Claude asked me targeted questions about my real UX experience.' },
          { number: '03', title: 'Ideate: Strategy & Positioning', content: 'Aligned on a strategy: keep honest job titles but lead every bullet with UX-facing language. Create a skills hierarchy that puts UX & Design tools first.' },
          { number: '04', title: 'Prototype: AI-Assisted Building', content: 'Claude generated the resume as clean, modern HTML/CSS. Built the portfolio site with expandable case studies, documenting the entire process.' },
          { number: '05', title: 'Test & Iterate', content: 'Reviewed outputs against the JD\'s specific requirements. Verified that every key ask was explicitly addressed. Iterated on language and structure.' }
        ]
      },
      {
        heading: 'What This Demonstrates',
        items: [
          'AI as a design multiplier: Used Claude not to replace thinking, but to accelerate analysis, surface gaps, and generate artifacts faster',
          'Design thinking process: Applied empathize \u2192 define \u2192 ideate \u2192 prototype \u2192 test, even to a non-product challenge',
          'Willingness to learn: First time using Claude; adopted it as a core tool in a real workflow within a single session',
          'Self-awareness: Recognized that my resume didn\'t tell my real story and used a structured process to fix it'
        ]
      }
    ],
    tools: 'Claude (Anthropic), VS Code, Intent Optimizer Protocol, HTML/CSS'
  }
];

function CaseStudyCard({ study }) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Card sx={{ mb: 4, bgcolor: 'rgba(255,255,255,0.05)', color: '#fff' }}>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          {study.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" sx={{ mr: 1, mb: 1, bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
          ))}
        </Box>
        <Typography variant="h5" gutterBottom>{study.title}</Typography>
        <Typography variant="subtitle1" color="grey.400" gutterBottom>{study.company}</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{study.summary}</Typography>

        <Collapse in={expanded}>
          {study.sections.map((section, i) => (
            <Box key={i} sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>{section.heading}</Typography>
              {section.content && (
                <Typography variant="body1">{section.content}</Typography>
              )}
              {section.items && (
                <List dense>
                  {section.items.map((item, j) => (
                    <ListItem key={j}>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              )}
              {section.steps && section.steps.map((step, j) => (
                <Box key={j} sx={{ display: 'flex', gap: 2, mb: 2, ml: 1 }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 700, minWidth: 40 }}>{step.number}</Typography>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>{step.title}</Typography>
                    <Typography variant="body2">{step.content}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Tools:</strong> {study.tools}</Typography>
          </Box>
        </Collapse>

        <Button
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mt: 2 }}
          color="secondary"
        >
          {expanded ? 'Collapse' : 'View Full Case Study'}
        </Button>
      </CardContent>
    </Card>
  );
}

export function CaseStudiesPage() {
  return (
    <Grid container justifyContent="center" spacing={1}>
      <Grid item xs={12} md={10} lg={8}>
        <Typography variant="h4" sx={{ color: '#fff', mb: 4, fontFamily: 'PerfectDark' }}>
          Case Studies
        </Typography>
        {caseStudies.map((study) => (
          <CaseStudyCard key={study.id} study={study} />
        ))}
      </Grid>
    </Grid>
  );
}
